import React from 'react';
import { CheckboxCheckedIcon, EditIcon3, DeleteIcon } from 'assets';
import { JobCriteriaData } from '../types';
import { WorkplaceQualitiesEditModal, DeleteSectionModal } from 'components';
import { useToast, useWorkCultureTraitsEdit, useWorkCultureTraitsDelete } from 'hooks';
import { WorkCultureTraitsPayload } from 'hooks';
import { toTitleCase } from 'utils/titleCase';

type WorkplaceQualitiesFormData = {
  workplaceQualities: string[];
  qualityOptions: { title: string; description?: string }[];
};

interface WorkplaceQualitiesSectionProps {
  assessmentId: string | null;
  data: JobCriteriaData['workplaceQualities'];
  workMotivators: JobCriteriaData['workMotivators'];
  onEdit: () => void;
  onDelete: () => void;
  onSave: (updatedData: JobCriteriaData['workplaceQualities']) => void;
  disabled?: boolean;
}

const WorkplaceQualitiesSection: React.FC<WorkplaceQualitiesSectionProps> = ({
  assessmentId,
  data,
  workMotivators,
  onEdit,
  onDelete,
  onSave,
  disabled,
}) => {
  const { openToast } = useToast();
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const {
    submitWorkCultureTraits,
    isSubmitting,
  } = useWorkCultureTraitsEdit(assessmentId);
  const {
    deleteWorkCultureTraits,
  } = useWorkCultureTraitsDelete(assessmentId);

  const onSubmit = (formData: WorkplaceQualitiesFormData) => {
    if (!assessmentId || isSubmitting) {
      openToast({
        show: true,
        heading: 'Required',
        text: 'Assessment ID is required',
        type: 'error',
        timeOut: 5000,
      });
      return;
    }

    const payload: WorkCultureTraitsPayload = {
      assessment_id: assessmentId,
      quality1: formData.workplaceQualities[0] || '',
      quality2: formData.workplaceQualities[1] || '',
      quality3: formData.workplaceQualities[2] || '',
      workCultureTraits: workMotivators.workMotivators.reduce((acc, trait, index) => ({
        ...acc,
        [`culture${index + 1}`]: trait.title,
        [`culture_expectation${index + 1}`]: trait.description,
        [`check${index + 1}`]: trait.checked,
      }), {}),
    };

    submitWorkCultureTraits(payload, {
      onSuccess: () => {
        const descriptionByTitle = new Map(
          (data.qualityOptions || []).map((option) => [option.title, option.description || ''])
        );
        onSave({
          workplaceQualities: formData.workplaceQualities.map((title) => ({
            title,
            description: descriptionByTitle.get(title) || '',
            checked: true,
          })),
          qualityOptions: formData.qualityOptions,
        });
        setIsEditModalOpen(false);
      },
      onError: () => {
        setIsEditModalOpen(false);
      },
    });
  };

  const handleDeleteConfirm = () => {
    if (!assessmentId) {
      openToast({
        show: true,
        heading: 'Required',
        text: 'Assessment ID is required for deletion',
        type: 'info',
        timeOut: 5000,
      });
      return;
    }

    deleteWorkCultureTraits({ assessment_id: assessmentId, section: 'workplace_quality' }, {
      onSuccess: () => {
        onDelete();
        setIsDeleteModalOpen(false);
      },
    });
  };

  return (
    <section className="border-t border-[#dfe1e6] pt-6 mb-8 relative">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-xl sm:text-2xl font-medium sm:font-semibold m-0">Workplace Qualities</h2>
        {!disabled && (
          <div className="flex gap-3.5 items-center">
            <span className="cursor-pointer">
              <EditIcon3 onClick={() => { onEdit(); setIsEditModalOpen(true); }} />
            </span>
            <div className="relative group cursor-pointer">
              <DeleteIcon onClick={() => setIsDeleteModalOpen(true)} />
              <div className="absolute bottom-full right-0 mb-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-52 z-10">
                <span className="block bg-[#1d2939] text-white text-xs rounded-lg px-3.5 py-2.5 leading-relaxed">
                  You can delete section you don't want to screen at this stage
                </span>
                <span className="absolute top-full right-[18px] w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px] border-t-[#1d2939]" />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        {data.workplaceQualities.map((quality, index) => (
          quality.checked && (
            <div key={index} className="flex gap-[17px] items-start">
              <div className="mt-1">
                <div className="w-7 h-7 rounded flex items-center justify-center">
                  <CheckboxCheckedIcon />
                </div>
              </div>
              <div className="flex-1">
                <p className="text-base font-semibold text-[#172b4d] m-0 mb-1">{toTitleCase(quality.title)}</p>
                {quality.description && (
                  <p className="text-sm font-normal leading-5 text-[#1D2939]">{quality.description.charAt(0).toUpperCase() + quality.description.slice(1)}</p>
                )}
              </div>
            </div>
          )
        ))}
      </div>

      <WorkplaceQualitiesEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        currentData={{
          workplaceQualities: data.workplaceQualities.map((quality) => quality.title),
          qualityOptions: data.qualityOptions,
        }}
        onSave={onSubmit}
        isSubmitting={isSubmitting}
      />

      <DeleteSectionModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        sectionName="Workplace Qualities"
      />
    </section>
  );
};

export default WorkplaceQualitiesSection;